import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { WithDestroy } from '@lib/shared';
import { Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { LoanApiService } from '../../../services/loan-api.service';
import { UserRolesResolve, UserService } from '../../../user';
import { UsersApiService } from '../../services/users-api.service';

@Component({
  selector: 'app-lead-detail',
  templateUrl: './lead-detail.component.html',
  styleUrls: ['./lead-detail.component.scss']
})
export class LeadDetailComponent extends WithDestroy() implements OnInit, OnDestroy {
  private readonly role = 'Loan Officer';
  private brokerUserNmls: number;
  public currentState: any;
  public options: any;
  public appStates: any = {
    ASK_LO: {
      eligible: false,
      completed: false
    },
    CHOOSE_LO: {
      eligible: false,
      completed: false
    }
  };
  public form: FormGroup;
  private defaultLeadAssigneeId: any;
  private defaultNMLS: any;
  public leadAssigneeId: any;
  public isBrokerAvailable = false;
  private searchSubscription: Subscription;
  public leadId: number;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loanApiService: LoanApiService,
    private userRolesResolve: UserRolesResolve,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private usersApiService: UsersApiService
  ) {
    super();
    this.leadId = +this.route.snapshot.paramMap.get('leadId');
    this.brokerUserNmls = this.route.snapshot.params.nmls;

    this.buildForm();
    this.userService
      .getActiveUser()
      .pipe(first())
      .subscribe((value: any) => {
        const nmlsLoanOfficerId = value?.LoanOfficerId || null;
        this.defaultLeadAssigneeId = value.WizeOrgUnit.Broker.LeadAssignee?.id || null;
        this.defaultNMLS = value.WizeOrgUnit.Broker.LeadAssignee?.nmlsId || null;
        this.isBrokerAvailable = this.isValidNmls(this.brokerUserNmls);
        if (!this.isBrokerAvailable || (this.isBrokerAvailable && !nmlsLoanOfficerId)) {
          if (this.defaultLeadAssigneeId) {
            this.currentState = 'ASK_LO';
            this.appStates.ASK_LO.eligible = true;
            this.appStates.CHOOSE_LO.eligible = true;
          } else {
            this.currentState = 'CHOOSE_LO';
            this.appStates.ASK_LO.eligible = false;
            this.appStates.CHOOSE_LO.eligible = true;
            this.getLoanOfficers('');
          }
        } else {
          this.currentState = 'DEFAULT';
          this.appStates.ASK_LO.eligible = false;
          this.appStates.CHOOSE_LO.eligible = false;
          this.leadAssigneeId = nmlsLoanOfficerId;
          this.convertToLoan();
        }
      });
  }

  ngOnInit() {}

  private buildForm() {
    this.form = this.formBuilder.group({
      isLoanOfficer: [null, [Validators.required]],
      loanOfficer: ['', [Validators.required]]
    });
    this.isLoanOfficer.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value) {
        this.resetLoanOfficer();
      } else {
        if (this.defaultLeadAssigneeId) {
          this.updateBroker(this.defaultLeadAssigneeId, this.defaultNMLS);
        }
        this.currentState = 'DEFAULT';
        this.convertToLoan();
      }
    });
  }

  public loanOfficerSelected(selectedLoanOfficer: any) {
    const loanOfficer = this.options.find((lo) => lo.id === selectedLoanOfficer.id);
    if (loanOfficer) {
      this.updateBroker(loanOfficer.id, loanOfficer.nmlsId);
    } else {
      this.loanOfficer.setErrors({ invalid: true });
      this.leadAssigneeId = null;
    }
  }

  convertToLoan() {
    this.loanApiService
      .convertLeadToLoan(this.leadId, {
        LoanOfficerId: this.leadAssigneeId || this.defaultLeadAssigneeId
      })
      .subscribe((value) => {
        this.userRolesResolve.resetCurrentUserWithACL();
        this.router.navigate([this.brokerUserNmls || '', 'pos', 'app', value]);
      });
  }

  private resetLoanOfficer() {
    this.loanOfficer.setValue('', { emitEvent: false });
    this.leadAssigneeId = null;
    this.currentState = 'CHOOSE_LO';
    this.getLoanOfficers('');
  }

  public back() {
    if (this.appStates.ASK_LO.eligible) {
      this.currentState = 'ASK_LO';
      this.isLoanOfficer.setValue(null, { emitEvent: false });
    }
  }

  public searchLoanOfficer(event) {
    this.leadAssigneeId = null;
    const searchedText = event.target.value.trim();
    if (!this.loanOfficer.invalid || searchedText.length === 0) {
      this.getLoanOfficers(searchedText);
    }
  }

  public getLoanOfficers(searchText) {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.searchSubscription = this.usersApiService.getUsers(searchText.trim(), this.role).subscribe(
      (los: any) =>
        (this.options = los?.map((lo) => {
          return {
            id: lo.id,
            name: lo.name,
            email: lo.email,
            nmlsId: lo.nmlsId,
            initials: `${lo.firstName.charAt(0)}${lo.lastName ? lo.firstName.charAt(0) : ''}`
          };
        }))
    );
  }

  public continue() {
    this.currentState = 'DEFAULT';
    this.convertToLoan();
  }

  private isValidNmls(nmls) {
    return /^[a-zA-Z0-9-]+$/.test(nmls);
  }

  private updateBroker(id: any, nmls: any) {
    this.leadAssigneeId = id;
    this.brokerUserNmls = nmls;
    sessionStorage.setItem('nmls', nmls);
  }

  get isLoanOfficer() {
    return this.form.get('isLoanOfficer');
  }

  get loanOfficer() {
    return this.form.get('loanOfficer');
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
